import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PageHead from "@/components/PageHead";
import SharedFooter from "@/components/SharedFooter";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { recordConsent } from "@/lib/policyConsent";

const POLICY = "privacy-terms";
const VERSION = "2026-10-v1";

/** Visitors confirm they have read the Privacy Policy and Terms; saved to their account. */
export default function PrivacyConsent() {
  const { user, loading } = useAuth();
  const [privacy, setPrivacy] = useState(false);
  const [terms, setTerms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [savedAt, setSavedAt] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) return;
    void supabase
      .from("policy_consents")
      .select("created_at")
      .eq("user_id", user.id)
      .eq("policy", POLICY)
      .eq("version", VERSION)
      .order("created_at", { ascending: false })
      .limit(1)
      .then(({ data }) => setSavedAt(data?.[0]?.created_at ?? null));
  }, [user]);

  const save = async () => {
    setBusy(true);
    setError(null);
    const ok = await recordConsent({ kind: "policy", policy: POLICY, choice: "agreed", version: VERSION });
    setBusy(false);
    if (ok) setSavedAt(new Date().toISOString());
    else setError("We could not save your confirmation. Please try again.");
  };

  const link = "text-primary underline underline-offset-2";

  return (
    <div className="theme-reading flex min-h-screen flex-col bg-background text-foreground">
      <PageHead title="Privacy consent | WeKIT™" description="Confirm you have read the WeKIT Privacy Policy and Terms." path="/privacy-consent" noIndex />
      <main className="mx-auto w-full max-w-xl flex-1 px-4 py-10">
        <h1 className="font-display text-3xl font-light tracking-tight">Privacy consent</h1>
        <p className="mt-2 text-[15px] leading-6 text-foreground/80">
          Please confirm you have read how we use your data and the terms for using WeKIT. Your confirmation is saved to your account.
        </p>

        {loading ? null : !user ? (
          <div className="mt-6 border border-border p-4 text-sm">
            Please <Link to="/auth?next=/privacy-consent" className={link}>sign in</Link> so your confirmation can be saved to your account.
          </div>
        ) : savedAt ? (
          <p role="status" className="mt-6 border border-primary/40 bg-primary/5 p-4 text-sm font-medium">
            Thank you. You confirmed on {new Date(savedAt).toLocaleDateString()}.
          </p>
        ) : (
          <div className="mt-6 space-y-3 border border-border p-4">
            <label className="flex cursor-pointer items-start gap-2 text-sm">
              <input type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} className="mt-0.5 h-4 w-4 accent-primary" />
              <span>I have read the <Link to="/privacy" className={link}>Privacy Policy</Link>.</span>
            </label>
            <label className="flex cursor-pointer items-start gap-2 text-sm">
              <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} className="mt-0.5 h-4 w-4 accent-primary" />
              <span>I have read and agree to the <Link to="/terms" className={link}>Terms &amp; Conditions</Link>.</span>
            </label>
            {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
            <button
              type="button"
              disabled={!privacy || !terms || busy}
              onClick={save}
              className="min-h-12 w-full bg-primary px-5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            >
              {busy ? "Saving…" : "Confirm and save"}
            </button>
          </div>
        )}
      </main>
      <SharedFooter />
    </div>
  );
}
